import { memo } from "react";
import { Dialog, DialogContent, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, User } from "lucide-react";
import { CustomerTemplate } from "@/components/CustomerTemplateManager";

interface DeleteConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerTemplates: CustomerTemplate[];
  customerIdToDelete: string | null;
  onConfirm: () => void;
}

/**
 * 고객 삭제 확인 다이얼로그 (presentational).
 * open/customerIdToDelete state와 삭제 로직(onConfirm)은 Home이 소유.
 * 원본의 IIFE quirk(`c.id === customerIdToDelete || ''`)를 그대로 보존.
 */
export const DeleteConfirmDialog = memo(function DeleteConfirmDialog({
  open,
  onOpenChange,
  customerTemplates,
  customerIdToDelete,
  onConfirm,
}: DeleteConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden rounded-2xl">
        <div className="bg-gradient-to-r from-teal-600 to-teal-500 px-5 py-4 text-white flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-white/90" />
          <DialogTitle className="text-base font-semibold tracking-tight">고객 삭제 확인</DialogTitle>
        </div>
        <div className="p-5 space-y-4">
          {(() => {
            const target = customerTemplates.find(c => c.id === customerIdToDelete || '');
            const name = target?.nickname || '해당';
            return (
              <>
                <div className="text-sm text-gray-700">
                  아래 고객을 삭제하시겠어요? 이 작업은 되돌릴 수 없습니다.
                </div>
                <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-full px-3 py-1 text-sm font-medium">
                  <User className="h-4 w-4" />
                  {name}
                </div>
                <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-md p-3 text-xs leading-relaxed">
                  삭제 시 해당 고객의 상품 목록, 메모가 삭제됩니다.
                  {/* DB FK ON DELETE CASCADE로 연결된 배송엑셀 행이 함께 지워진다. 로컬·비회원 고객은 배송 행이 없다. */}
                  {customerIdToDelete?.startsWith('order_') && (
                    <>
                      <br />
                      배송엑셀에 입력한 이 고객의 배송정보도 함께 삭제됩니다.
                    </>
                  )}
                </div>
              </>
            );
          })()}
        </div>
        <DialogFooter className="px-5 pb-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>취소</Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
          >
            삭제
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
